/**
 * EXTRACTION HEALTH MONITORING HOOK
 * Real-time health status monitoring for post-message extraction pipeline
 * 
 * Phase 1: Console feedback for testing
 * Phase 2: Admin portal integration (future)
 */

import { useEffect, useState, useCallback } from 'react';
import { apiGet } from '../utils/api';

interface ExtractionHealthDetails {
  extractors_executed: number;
  extractors_queued: number;
  storage_operations: number;
  critical_failures: number;
  warnings: number;
  latency_ms: number;
}

interface ExtractionHealth {
  status: 'healthy' | 'degraded' | 'failing' | 'critical' | 'unknown';
  health_score: number;
  message: string;
  details: ExtractionHealthDetails;
  full_report: any;
}

export const useExtractionHealthMonitor = () => {
  const [lastHealth, setLastHealth] = useState<ExtractionHealth | null>(null);
  const [isMonitoring, setIsMonitoring] = useState(true);

  /**
   * Check extraction health for a specific message
   */
  const checkMessageHealth = useCallback(async (messageId: string) => {
    if (!isMonitoring) return;

    try {
      const response = await apiGet(`/api/health/extraction-status/${messageId}`);
      const health: ExtractionHealth = await response.json();

      // Update state
      setLastHealth(health);

      // Display in console for testing
      displayHealthInConsole(health);

      return health;
    } catch (error) {
      console.error('❌ Failed to fetch extraction health:', error);
      return null;
    }
  }, [isMonitoring]);

  /**
   * Check latest extraction health for current user
   */
  const checkLatestHealth = useCallback(async () => {
    if (!isMonitoring) return;

    try {
      const response = await apiGet('/api/health/latest');
      const health: ExtractionHealth = await response.json();

      // Update state
      setLastHealth(health);

      // Display in console for testing
      displayHealthInConsole(health);

      return health;
    } catch (error) {
      console.error('❌ Failed to fetch latest extraction health:', error);
      return null;
    }
  }, [isMonitoring]);

  /**
   * Display health status in console with styled output
   */
  const displayHealthInConsole = (health: ExtractionHealth) => {
    // Create styled console output
    const styles = {
      healthy: 'color: #22c55e; font-weight: bold; font-size: 14px;',
      degraded: 'color: #f59e0b; font-weight: bold; font-size: 14px;',
      failing: 'color: #ef4444; font-weight: bold; font-size: 14px;',
      critical: 'color: #dc2626; font-weight: bold; font-size: 16px; text-decoration: underline;',
      unknown: 'color: #9ca3af; font-weight: bold; font-size: 14px;'
    };

    const style = styles[health.status] || styles.unknown;

    // Display main message
    console.log(`%c${health.message}`, style);

    // Display details if not healthy
    if (health.status !== 'healthy' && health.status !== 'unknown') {
      console.group('Extraction Health Details');
      console.log('Health Score:', `${(health.health_score * 100).toFixed(1)}%`);
      console.log('Status:', health.status.toUpperCase());
      console.log('Extractors Executed:', health.details.extractors_executed);
      console.log('Extractors Queued:', health.details.extractors_queued);
      console.log('Storage Operations:', health.details.storage_operations);
      console.log('Critical Failures:', health.details.critical_failures);
      console.log('Warnings:', health.details.warnings);
      console.log('Total Latency:', `${health.details.latency_ms}ms`);
      
      // Display full report for debugging
      if (health.full_report) {
        console.log('Full Report:', health.full_report);
      }
      
      console.groupEnd();
    }
  };

  /**
   * Enable/disable monitoring
   */
  const setMonitoring = useCallback((enabled: boolean) => {
    setIsMonitoring(enabled);
    console.log(`🏥 Extraction health monitoring ${enabled ? 'enabled' : 'disabled'}`);
  }, []);

  /**
   * Alias for checkMessageHealth (for backward compatibility)
   */
  const logExtraction = checkMessageHealth;

  return {
    lastHealth,
    checkMessageHealth,
    checkLatestHealth,
    logExtraction,
    setMonitoring,
    isMonitoring
  };
};

